import { CheckCircle2Icon, Loader2Icon, Trash2Icon, XIcon } from 'lucide-react'
import { type ReactNode } from 'react'

import {
  SettingsPageSaveButtons,
  type SettingsPageSaveButtonsProps,
} from '@/pages/settings-page/settings-page-save-buttons'

import { Badge } from '@/components/lemmala-design-system/badge/badge'
import {
  Breadcrumbs,
  type BreadcrumbsProps,
} from '@/components/lemmala-design-system/breadcrumbs/breadcrumbs'
import { Button } from '@/components/lemmala-design-system/button/button'
import { Separator } from '@/components/lemmala-design-system/separator/separator'
import { TextWithIcon } from '@/components/lemmala-design-system/text-with-icon/text-with-icon'

export type DrillInPageHeaderDeleteAction = {
  onDelete: () => void
  isDisabled?: boolean
}

export type DrillInPageHeaderEnableDisableAction = {
  isEnabled: boolean
  onToggle?: () => void
  isPending?: boolean
}

export type DrillInPageHeaderProps = {
  breadcrumbs?: BreadcrumbsProps['items']
  onBreadcrumbItemClick?: BreadcrumbsProps['onItemClick']
  additionalActions?: ReactNode
  deleteAction?: DrillInPageHeaderDeleteAction
  enableDisableAction?: DrillInPageHeaderEnableDisableAction
  roleToggle?: ReactNode
  saveAction?: SettingsPageSaveButtonsProps
}

export const DrillInPageHeader = ({
  breadcrumbs,
  onBreadcrumbItemClick,
  additionalActions,
  deleteAction,
  enableDisableAction,
  roleToggle,
  saveAction,
}: DrillInPageHeaderProps) => {
  const hasActions = !!(
    additionalActions ||
    deleteAction ||
    enableDisableAction ||
    roleToggle ||
    saveAction
  )

  if (!breadcrumbs && !hasActions) {
    return null
  }

  const toggleButton = enableDisableAction?.onToggle && (
    <Button
      variant={enableDisableAction.isEnabled ? 'secondary' : 'promotion'}
      onClick={enableDisableAction.onToggle}
      disabled={enableDisableAction.isPending}
    >
      <TextWithIcon
        text={enableDisableAction.isEnabled ? 'Disable' : 'Enable'}
        icon={
          enableDisableAction.isPending ? (
            <Loader2Icon className='size-4 animate-spin' />
          ) : enableDisableAction.isEnabled ? (
            <XIcon className='size-4' />
          ) : (
            <CheckCircle2Icon className='size-4' />
          )
        }
      />
    </Button>
  )

  return (
    <div className='flex h-13 shrink-0 items-center justify-between gap-4 border-b px-6'>
      <div className='min-w-0 flex-1 contain-inline-size'>
        {breadcrumbs && <Breadcrumbs items={breadcrumbs} onItemClick={onBreadcrumbItemClick} />}
      </div>
      {hasActions && (
        <div className='flex shrink-0 items-center gap-3'>
          {additionalActions && <div className='flex items-center gap-2'>{additionalActions}</div>}
          {enableDisableAction && !enableDisableAction.isEnabled && (
            <Badge variant='danger'>
              <TextWithIcon text='Disabled' icon={<XIcon className='size-4' />} />
            </Badge>
          )}
          {deleteAction && (
            <Button
              variant='ghost'
              onClick={deleteAction.onDelete}
              disabled={deleteAction.isDisabled}
            >
              <Trash2Icon className='size-4' />
            </Button>
          )}
          {deleteAction && toggleButton && <Separator className='h-8' orientation='vertical' />}
          {toggleButton}
          {roleToggle}
          {saveAction && <SettingsPageSaveButtons {...saveAction} />}
        </div>
      )}
    </div>
  )
}
